import AppIcon from "common/app-icon";
import type { GlideFC } from "lib/glide-fc";
import styled, { css } from "styled-components";

export type StrokeIcons =
    | "st-arrow-external"
    | "st-arrow-reload"
    | "st-book"
    | "st-caret"
    | "st-checkmark"
    | "st-chevrons-vertical"
    | "st-close"
    | "st-data-editor"
    | "st-edit-pencil"
    | "st-edit-pencil2"
    | "st-form-factor-desktop"
    | "st-form-factor-phone"
    | "st-form-factor-tablet"
    | "st-half-spinner"
    | "st-menu-checkmark"
    | "st-notebook"
    | "st-open-external"
    | "st-page"
    | "st-people"
    | "st-plus-stroke"
    | "st-project-app"
    | "st-project-page"
    | "st-reload-arrow"
    | "st-search"
    | "st-success-check"
    | "st-trash"
    | "st-arrow-full"
    | "st-circle"
    | "st-italics-cursor";
export type TwotoneIcons =
    | "tt-glide-table"
    | "tt-large-play"
    | "tt-containing-screen-context"
    | "tt-containing-screen-overlay"
    | "tt-edit-pencil"
    | "tt-header-string"
    | "tt-file-upload"
    | "tt-player-camera"
    | "tt-glide-circle"
    | "tt-excel-sheet"
    | "tt-google-sheet"
    | "tt-pages-crm"
    | "tt-pages-delivery"
    | "tt-pages-field-sales"
    | "tt-pages-inventory"
    | "tt-pages-property-management";
export type MonotoneIcons =
    | "mt-add-new-small"
    | "mt-airtable-sheet-WIP"
    | "mt-avatar-empty"
    | "mt-bar-chart"
    | "mt-border-3by2"
    | "mt-border-circle"
    | "mt-border-square"
    | "mt-center-gravity"
    | "mt-chevron-left"
    | "mt-chevron-right"
    | "mt-clear"
    | "mt-component-big-numbers"
    | "mt-component-breadcrumbs"
    | "mt-component-button-bar"
    | "mt-component-button"
    | "mt-component-collection"
    | "mt-component-container"
    | "mt-component-favorite"
    | "mt-component-fields"
    | "mt-component-hint"
    | "mt-component-image"
    | "mt-component-rich-text"
    | "mt-component-separator"
    | "mt-component-custom"
    | "mt-component-video"
    | "mt-donut-chart-reverse"
    | "mt-donut-chart"
    | "mt-dots-horizontal"
    | "mt-duplicate"
    | "mt-enum-choice-chips"
    | "mt-enum-choice-radio"
    | "mt-excel"
    | "mt-face-gravity"
    | "mt-glide-app-icon"
    | "mt-glide-page-icon"
    | "mt-g-logo"
    | "mt-g-sheet"
    | "mt-glide-logo"
    | "mt-glide-table-fixed-color"
    | "mt-info-alert"
    | "mt-info-circle"
    | "mt-lock"
    | "mt-menu"
    | "mt-more-vertical"
    | "mt-no-color-g-sheet"
    | "mt-pages-directory"
    | "mt-pages-blank"
    | "mt-pages-portal"
    | "mt-pages-form"
    | "mt-page-file-upload"
    | "mt-page-image-upload"
    | "mt-page-calendar"
    | "mt-pie-chart"
    | "mt-platform-android"
    | "mt-platform-ios"
    | "mt-play"
    | "mt-random-sort"
    | "mt-resize-h"
    | "mt-settings-gear"
    | "mt-stacked-bar-chart"
    | "mt-text-area"
    | "mt-users"
    | "mt-avatar-empty"
    | "mt-chevron-left"
    | "mt-chevron-right"
    | "mt-circular-cross"
    | "mt-play"
    | "mt-warning"
    | "mt-cursor";

export type AnyIcon = StrokeIcons | TwotoneIcons | MonotoneIcons;

export function isGlideIcon(icon: string | undefined): icon is AnyIcon {
    return isStrokeIcon(icon) || isTwotoneIcon(icon) || isMonotoneIcon(icon);
}

export function isStrokeIcon(icon: string | undefined): icon is StrokeIcons {
    return icon !== undefined && icon.startsWith("st-");
}

export function isTwotoneIcon(icon: string | undefined): icon is TwotoneIcons {
    return icon !== undefined && icon.startsWith("tt-");
}

export function isMonotoneIcon(icon: string | undefined): icon is MonotoneIcons {
    return icon !== undefined && icon.startsWith("mt-");
}

export function backCompatIcon(icon: string, size: number) {
    if (isStrokeIcon(icon)) {
        return <GlideIcon kind="stroke" icon={icon} iconSize={size} />;
    } else if (isTwotoneIcon(icon)) {
        return <GlideIcon kind="twotone" icon={icon} iconSize={size} />;
    } else if (isMonotoneIcon(icon)) {
        return <GlideIcon kind="monotone" icon={icon} iconSize={size} />;
    } else {
        return <AppIcon icon={icon} size={size} />;
    }
}

interface BaseIconProps {
    iconSize?: number;
    rotateDeg?: number;
    spin?: boolean;
}

interface MonotoneProps extends BaseIconProps {
    kind: "monotone";
    icon: MonotoneIcons;
    fgColor?: string;
}

interface TwotoneProps extends BaseIconProps {
    kind: "twotone";
    icon: TwotoneIcons;
    primaryColor?: string;
    accentColor?: string;
}

interface StrokeProps extends BaseIconProps {
    kind: "stroke";
    icon: StrokeIcons;
    strokeColor?: string;
    strokeWidth?: number;
}

type StyleProps = MonotoneProps | TwotoneProps | StrokeProps;

type Props = StyleProps & {
    className?: string;
};

const GlideIconStyle = styled.div<Props>`
    width: ${p => p.iconSize ?? 24}px;
    height: ${p => p.iconSize ?? 24}px;

    position: relative;
    display: flex;
    & svg {
        ${p =>
            p.kind === "twotone"
                ? css`
                      ${p.primaryColor === undefined ? "" : `--primary-color: ${p.primaryColor}`};
                      ${p.accentColor === undefined ? "" : `--accent-color: ${p.accentColor}`};
                  `
                : ""};

        ${p =>
            p.kind === "monotone"
                ? css`
                      ${p.fgColor === undefined ? "" : `--fg-color: ${p.fgColor}`};
                  `
                : ""};

        ${p =>
            p.kind === "stroke"
                ? css`
                      ${p.strokeColor === undefined ? "" : `--stroke-color: ${p.strokeColor}`};
                      ${p.strokeWidth === undefined ? "" : `--stroke-width: ${p.strokeWidth}`};
                  `
                : ""};

        ${p =>
            p.spin === true &&
            css`
                @keyframes spin {
                    100% {
                        -webkit-transform: rotate(360deg);
                        transform: rotate(360deg);
                    }
                }
                animation: spin 0.7s linear infinite;
            `};

        stroke-width: 1.5;
        fill: currentColor;
        stroke: currentColor;

        width: 100%;
        height: 100%;

        transform: rotate(${p => p.rotateDeg ?? 0}deg);

        transition: transform 0.15s ease;
    }
`;

export const GlideIcon: GlideFC<Props> = p => {
    const { icon, kind } = p;
    return (
        <GlideIconStyle {...p}>
            <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
                <use xlinkHref={`/svg/${kind}/${icon}.svg#icon-import`} />
            </svg>
        </GlideIconStyle>
    );
};
